"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import "../settlement.css";

type Holding = { code: string; name: string; shares: number; cost: number; price: number; value: number; profit: number; pct: number };
type RecordItem = {
  id: string; profile: string; ruleMode?: string; initialCash: number; cash: number; total: number; profit: number; returnPct: number;
  startedAt: string; endedAt: string; duration: string; tradeCount: number; maxGain: number; maxLoss: number; holdings: Holding[];
};

const money = (n: number) => new Intl.NumberFormat("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function Settlement() {
  const [current, setCurrent] = useState<RecordItem | null>(null);
  const [history, setHistory] = useState<RecordItem[]>([]);

  useEffect(() => {
    try {
      const saved = (JSON.parse(localStorage.getItem("hongpan-history-v1") || "[]") as RecordItem[]).slice(0, 15);
      if (saved.length !== JSON.parse(localStorage.getItem("hongpan-history-v1") || "[]").length) localStorage.setItem("hongpan-history-v1", JSON.stringify(saved));
      setHistory(saved);
      const params = new URLSearchParams(window.location.search);
      const detailId = params.get("id");
      setCurrent(detailId ? saved.find((item) => item.id === detailId) || null : params.has("history") ? null : JSON.parse(localStorage.getItem("hongpan-last-settlement") || "null"));
    } catch {}
  }, []);

  function deleteRecord(id: string) {
    if (!window.confirm("确认删除这条历史模拟记录？删除后无法恢复。")) return;
    const next = history.filter((item) => item.id !== id);
    setHistory(next);
    localStorage.setItem("hongpan-history-v1", JSON.stringify(next));
    if (current?.id === id) { localStorage.removeItem("hongpan-last-settlement"); setCurrent(null); window.history.replaceState(null, "", "/settlement?history=1"); }
  }

  function restart() {
    localStorage.removeItem("hongpan-v3");
    localStorage.removeItem("hongpan-last-settlement");
    window.location.href = "/";
  }

  return <main className="settlement-page">
    <header><Link href="/">红盘</Link><span>模拟结算中心</span></header>
    <div className="settlement-wrap">
      {current ? <>
        <p className="eyebrow">SIMULATION COMPLETE</p>
        <h1>本轮模拟已结算</h1>
        <p className="settlement-lead">{current.profile} · {new Date(current.endedAt).toLocaleString("zh-CN")} · 结果已计入个人历史</p>
        <section className="score-grid">
          <article className="primary"><span>期末总资产</span><strong>¥ {money(current.total)}</strong><small>初始资金 ¥ {money(current.initialCash)}</small></article>
          <article><span>累计收益</span><strong className={current.profit >= 0 ? "positive" : "negative"}>{current.profit >= 0 ? "+" : ""}¥ {money(current.profit)}</strong><small>{current.returnPct >= 0 ? "+" : ""}{current.returnPct.toFixed(2)}%</small></article>
          <article><span>模拟周期</span><strong>{current.duration}</strong><small>{new Date(current.startedAt).toLocaleDateString("zh-CN")} 开始</small></article>
          <article><span>交易规则</span><strong>{current.ruleMode || "规则未记录"}</strong><small>{current.ruleMode === "轻量规则" ? "免手续费 · 数量自由" : current.ruleMode === "真实规则" ? "标准单位 · 交易费用" : "旧版历史记录"}</small></article><article><span>成交次数</span><strong>{current.tradeCount}</strong><small>笔模拟成交</small></article>
          <article><span>单笔最大上涨</span><strong className="positive">+{current.maxGain.toFixed(2)}%</strong><small>按本轮持仓浮动统计</small></article>
          <article><span>单笔最大下跌</span><strong className="negative">{current.maxLoss.toFixed(2)}%</strong><small>按本轮持仓浮动统计</small></article>
        </section>
        <section className="settlement-panel">
          <div className="settlement-title"><div><small>PORTFOLIO AT CLOSE</small><h2>结算持仓</h2></div><b>现金 ¥ {money(current.cash)}</b></div>
          <div className="settlement-table"><table><thead><tr><th>产品</th><th>持有数量</th><th>成本价</th><th>结算价</th><th>市值</th><th>盈亏</th></tr></thead>
          <tbody>{current.holdings.length ? current.holdings.map((h) => <tr key={h.code}><td><b>{h.name}</b><small>{h.code}</small></td><td>{h.shares}</td><td>{money(h.cost)}</td><td>{money(h.price)}</td><td>{money(h.value)}</td><td className={h.profit >= 0 ? "positive" : "negative"}>{h.profit >= 0 ? "+" : ""}{money(h.profit)}<small>{h.pct >= 0 ? "+" : ""}{h.pct.toFixed(2)}%</small></td></tr>) : <tr><td colSpan={6} className="no-holdings">本轮以零持仓结束</td></tr>}</tbody></table></div>
        </section>
      </> : <><p className="eyebrow">PERSONAL ARCHIVE</p><h1>历史模拟</h1><p className="settlement-lead">保存在当前设备上的每次结算记录。</p></>}

      <section className="settlement-panel ranking">
        <div className="settlement-title"><div><small>PERSONAL RANKING</small><h2>个人历史排行榜</h2></div><span>按收益率排序</span></div>
        {history.length ? [...history].sort((a,b) => b.returnPct-a.returnPct).map((item, index) => <article key={item.id}>
          <i>{index + 1}</i><div><b>{item.profile}</b><small>{new Date(item.endedAt).toLocaleString("zh-CN")} · {item.duration} · {item.ruleMode || "规则未记录"}</small></div>
          <strong className={item.returnPct >= 0 ? "positive" : "negative"}>{item.returnPct >= 0 ? "+" : ""}{item.returnPct.toFixed(2)}%</strong><span>¥ {money(item.total)}</span>
          <div className="record-actions"><button onClick={() => window.location.href=`/settlement?id=${encodeURIComponent(item.id)}`}>查看详情</button><button onClick={() => deleteRecord(item.id)}>删除</button></div>
        </article>) : <div className="empty-history">还没有完成过模拟，完成第一轮后会出现在这里。</div>}
      </section>
      <button className="restart-button" onClick={restart}>返回初始界面 · 重新选择身份</button>
      <p className="settlement-note">返回后将清除当前进行中的账户，历史结算记录会继续保留。</p>
    </div>
  </main>;
}
